//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

//Local libs
import Service from 'src/app/common/service';
import TreatmentTypeModel from './treatmentType.model';
import { Subscription } from 'rxjs';

/**
 * @service TreatmentTypeService
 * @description Service for managing Treatment Type definitions within the aquaculture system.
 * Treatment Types categorize the nature of treatments applied to ponds, such as:
 * - **Medicine**: Antibiotics or therapeutic agents.
 * - **Probiotic**: Beneficial bacteria for water or gut health.
 * - **Chemical**: Disinfectants, lime, or other water conditioners.
 * - **Feed Additive**: Supplements mixed with feed.
 *
 * This service provides the interface to retrieve these definitions from the backend API.
 * It is essential for:
 * 1. **Data Entry**: Populating dropdowns in treatment logs.
 * 2. **Reporting**: Categorizing treatments for analysis.
 * 3. **Validation**: Ensuring correct units and application methods are used for each type.
 *
 * The service interacts with the `/blubay/treatmenttype/` backend endpoint.
 *
 * @feature 1. Retrieve all defined Treatment Types.
 * @feature 2. Support for large datasets (pageSize=2000) to ensure all types are loaded.
 * @feature 3. Robust management of concurrent HTTP requests to prevent memory leaks.
 *
 * @architecture
 * Extends base Service<TreatmentTypeModel> providing REST API integration.
 * Uses HttpClient for HTTP operations and manages subscriptions for cleanup.
 *
 * @service-endpoint /blubay/treatmenttype/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class TreatmentTypeService extends Service<TreatmentTypeModel> {
  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions.
   * Used to maintain references to active Observables so they can be unsubscribed
   * when the service is destroyed or when cancellation is requested.
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient.
   *
   * Initializes the service by calling the parent Service constructor with the
   * HttpClient instance and the specific API endpoint path (`/blubay/treatmenttype/`).
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests to the backend.
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/blubay/treatmenttype/');
  }

  /**
   * @method getAllTreatmentTypes
   * @async
   * @description Fetch all Treatment Type records from the backend.
   *
   * This method performs a GET request to the service endpoint. It is configured to retrieve
   * a large number of records (pageSize=2000) to ensure all types are loaded,
   * ordered by creation date ('new').
   *
   * @returns {Promise<Array<TreatmentTypeModel>>} Promise resolving to an array of TreatmentTypeModel objects.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP GET request.
   * - Pushes the subscription to `ongoingRequests`.
   *
   * @example
   * treatmentTypeService.getAllTreatmentTypes().then(types => {
   *   console.log('Treatment Types:', types);
   * });
   */
  public getAllTreatmentTypes() {
    return new Promise<Array<TreatmentTypeModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<TreatmentTypeModel>>(`${this.baseUrl}${this.serviceUrl}?pageSize=2000&order=new`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  //To Cancel the onGoing Http Requests
  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks.
   *
   * This method iterates through the `ongoingRequests` array and calls `unsubscribe()`
   * on each subscription. This is typically called when a component using this service
   * is destroyed (ngOnDestroy) to ensure that no network requests continue in the background.
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests tracked in `ongoingRequests`.
   * - Resets `ongoingRequests` to an empty array.
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
